/**
 * Main-thread side of search: keeps a Dexie mirror of note text (so reboots
 * don't re-read every note doc), feeds the worker, exposes search().
 */
import Dexie, { type EntityTable } from "dexie";
import type { WorkspaceStore } from "../workspace";
import type { NoteDocManager } from "../notes";
import type { IndexDoc, SearchHit, WorkerRequest, WorkerResponse } from "./worker";

interface IndexRow extends IndexDoc {
  updatedAt: number;
  /** normalized [[wikilink]] targets found in the note body */
  links: string[];
  /** #hashtags found in the note body (lowercased, no #) */
  hashtags: string[];
  /** attachment node keys referenced by the body (multiEntry indexed) */
  attachments: string[];
  /** attachment key -> display name. Cosmetic, not indexed. */
  attachmentNames: Record<string, string>;
  /** attachment key -> href, for our own /api/attachments refs. Not indexed. */
  attachmentHrefs: Record<string, string>;
}

/** What the graph needs from a row. `text` is dropped unless asked for. */
export interface GraphIndexRow {
  id: string;
  title: string;
  links: string[];
  hashtags: string[];
  attachments: string[];
  attachmentNames: Record<string, string>;
  attachmentHrefs: Record<string, string>;
}

const WIKILINK_RE = /\[\[([^\]|\n]+)(?:\|[^\]\n]*)?\]\]/g;
const HASHTAG_RE = /(?:^|[\s(])#([a-z][\w/-]*)/gi;

/** `[text](url)` and `![alt](url)`, tolerating <angle> urls and a "title". */
const MD_LINK_RE = /(!?)\[([^\]\n]*)\]\(\s*<?([^)\s>]+)>?(?:\s+"[^"]*")?\s*\)/g;
/** `![[file.png]]` embeds, as produced by the Obsidian importer. */
const EMBED_WIKILINK_RE = /!\[\[([^\]|\n]+?)(?:\|[^\]\n]*)?\]\]/g;
/** Our own uploads. Relative or absolute; the id is the path segment. */
const ATTACHMENT_URL_RE = /^(?:https?:\/\/[^/]+)?\/api\/attachments\/([A-Za-z0-9_-]{4,})(?:[/?#].*)?$/;
/** Vault-relative files carried in by the Obsidian/Notion/Evernote importers. */
const MEDIA_EXT_RE =
  /\.(png|jpe?g|gif|webp|avif|svg|bmp|ico|mp4|webm|mov|m4v|mp3|wav|ogg|flac|m4a|pdf|zip|docx?|xlsx?|pptx?|csv)$/i;

export function extractHashtags(markdown: string): string[] {
  const out = new Set<string>();
  // strip fenced code so #comments in code don't count
  const withoutCode = markdown.replace(/```[\s\S]*?```/g, "");
  for (const m of withoutCode.matchAll(HASHTAG_RE)) {
    out.add(m[1]!.toLowerCase());
  }
  return [...out];
}

export function extractWikilinkTargets(markdown: string): string[] {
  const out = new Set<string>();
  for (const m of markdown.matchAll(WIKILINK_RE)) {
    const target = m[1]?.trim().toLowerCase();
    if (!target) continue;
    // `![[a.png]]` is a file embed, not a link to a note. Without this the
    // graph mints a phantom node for every embedded image.
    const isEmbed = m.index > 0 && markdown[m.index - 1] === "!";
    if (isEmbed && MEDIA_EXT_RE.test(target)) continue;
    out.add(target);
  }
  return [...out];
}

export interface AttachmentRef {
  /** Stable node key: the attachment id, or the lowercased relative path. */
  key: string;
  name: string;
  /** Present only when the ref points at our own attachment route. */
  href?: string;
}

/**
 * Files a note references. Two shapes count: our own /api/attachments uploads
 * (both `![img](url)` and the `[name](url)` form the editor emits for
 * non-images, or PDFs would be invisible), and scheme-less relative paths with
 * a known media extension, which is what the vault importers leave behind.
 *
 * Every other http(s) URL is deliberately NOT an attachment: Obsidian's graph
 * has no external-web nodes and adding them would swamp a research vault.
 */
export function extractAttachmentRefs(markdown: string): AttachmentRef[] {
  const out = new Map<string, AttachmentRef>();
  // strip fenced code, same as the hashtag extractor
  const src = markdown.replace(/```[\s\S]*?```/g, "");

  for (const m of src.matchAll(MD_LINK_RE)) {
    const text = (m[2] ?? "").trim();
    const url = m[3] ?? "";
    const id = ATTACHMENT_URL_RE.exec(url)?.[1];
    if (id) {
      out.set(id, { key: id, name: text || id, href: url });
      continue;
    }
    const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(url);
    if (hasScheme || url.startsWith("#") || url.startsWith("//")) continue;
    if (!MEDIA_EXT_RE.test(url)) continue;
    const key = decodeURIComponent(url).trim().toLowerCase();
    if (key) out.set(key, { key, name: text || key.split("/").pop() || key });
  }

  for (const m of src.matchAll(EMBED_WIKILINK_RE)) {
    const raw = (m[1] ?? "").trim();
    // `![[Some Note]]` is a transclusion, not a file
    if (!raw || !MEDIA_EXT_RE.test(raw)) continue;
    const key = raw.toLowerCase();
    out.set(key, { key, name: raw });
  }

  return [...out.values()];
}

/** Rows predate v4 in the wild, so the attachment fields are defaulted. */
function toGraphRow(r: IndexRow): GraphIndexRow {
  return {
    id: r.id,
    title: r.title,
    links: r.links ?? [],
    hashtags: r.hashtags ?? [],
    attachments: r.attachments ?? [],
    attachmentNames: r.attachmentNames ?? {},
    attachmentHrefs: r.attachmentHrefs ?? {},
  };
}

class SearchService {
  private worker: Worker | null = null;
  private db: Dexie & { noteIndex: EntityTable<IndexRow, "id"> };
  private workspace: WorkspaceStore | null = null;
  private notes: NoteDocManager | null = null;
  private unsubscribe: (() => void) | null = null;
  private nextRequestId = 1;
  private pending = new Map<number, (hits: SearchHit[]) => void>();
  private reconcileTimer: ReturnType<typeof setTimeout> | null = null;
  private lastSeen = new Map<string, number>();
  private indexedListeners = new Set<() => void>();
  /** Bumped whenever a reconcile actually changed something. */
  revision = 0;

  constructor() {
    this.db = new Dexie("hn.search") as SearchService["db"];
    this.db.version(1).stores({ noteIndex: "id, updatedAt" });
    this.db.version(2).stores({ noteIndex: "id, updatedAt, *links" });
    this.db.version(3).stores({ noteIndex: "id, updatedAt, *links, *hashtags" });
    // v4 adds attachment refs. reconcile() only re-reads rows whose stored
    // updatedAt is behind the item's, and lastSeen is seeded from this mirror,
    // so without forcing a re-read existing notes would never gain
    // `attachments` and the graph would show them only for notes edited after
    // the upgrade. -1 rather than clear() keeps full-text search working
    // through the upgrade while guaranteeing exactly one full re-read pass.
    this.db
      .version(4)
      .stores({ noteIndex: "id, updatedAt, *links, *hashtags, *attachments" })
      .upgrade((tx) =>
        tx
          .table("noteIndex")
          .toCollection()
          .modify((row: IndexRow) => {
            row.updatedAt = -1;
          }),
      );
  }

  /** Fires after any reconcile that changed the index. */
  onIndexed(cb: () => void): () => void {
    this.indexedListeners.add(cb);
    return () => this.indexedListeners.delete(cb);
  }

  attach(workspace: WorkspaceStore, notes: NoteDocManager) {
    this.workspace = workspace;
    this.notes = notes;
    this.worker = new Worker(new URL("./worker.ts", import.meta.url), { type: "module" });
    this.worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
      if (e.data.type === "results") {
        this.pending.get(e.data.requestId)?.(e.data.hits);
        this.pending.delete(e.data.requestId);
      }
    };
    void this.boot();
    this.unsubscribe = workspace.subscribe(() => this.scheduleReconcile());
  }

  detach() {
    this.unsubscribe?.();
    this.worker?.terminate();
    this.worker = null;
    this.workspace = null;
    this.notes = null;
    this.lastSeen.clear();
  }

  private post(msg: WorkerRequest) {
    this.worker?.postMessage(msg);
  }

  private async boot() {
    // 1) hydrate worker from the Dexie mirror (no note docs opened)
    const rows = await this.db.noteIndex.toArray();
    if (rows.length) this.post({ type: "upsert", docs: rows });
    for (const r of rows) this.lastSeen.set(r.id, r.updatedAt);
    // 2) reconcile against current workspace state
    await this.workspace?.whenLoaded;
    this.scheduleReconcile();
  }

  private scheduleReconcile() {
    if (this.reconcileTimer) clearTimeout(this.reconcileTimer);
    this.reconcileTimer = setTimeout(() => void this.reconcile(), 400);
  }

  /** Diff workspace items against the mirror; re-read only stale notes. */
  private async reconcile() {
    const ws = this.workspace;
    const notes = this.notes;
    if (!ws || !notes) return;

    const liveNotes = ws
      .getSnapshot()
      .filter((i) => (i.type === "note" || i.type === "card") && !i.trashedAt && !i.archivedAt);
    const liveIds = new Set(liveNotes.map((i) => i.id));

    let changed = false;

    // removals (trashed or deleted)
    const removed = [...this.lastSeen.keys()].filter((id) => !liveIds.has(id));
    if (removed.length) {
      this.post({ type: "remove", ids: removed });
      await this.db.noteIndex.bulkDelete(removed);
      for (const id of removed) this.lastSeen.delete(id);
      changed = true;
    }

    // stale or new: read once, feed worker + mirror in batches
    const stale = liveNotes.filter((i) => (this.lastSeen.get(i.id) ?? -1) < i.updatedAt);
    let batch: IndexRow[] = [];
    const flush = async () => {
      if (!batch.length) return;
      const rows = batch;
      batch = [];
      this.post({ type: "upsert", docs: rows });
      await this.db.noteIndex.bulkPut(rows);
    };
    for (const item of stale) {
      const text = await notes.readMarkdown(item.id);
      const refs = extractAttachmentRefs(text);
      batch.push({
        id: item.id,
        title: item.title,
        text,
        tags: item.tags,
        updatedAt: item.updatedAt,
        links: extractWikilinkTargets(text),
        hashtags: extractHashtags(text),
        attachments: refs.map((r) => r.key),
        attachmentNames: Object.fromEntries(refs.map((r) => [r.key, r.name])),
        attachmentHrefs: Object.fromEntries(
          refs.filter((r) => r.href).map((r) => [r.key, r.href as string]),
        ),
      });
      this.lastSeen.set(item.id, item.updatedAt);
      changed = true;
      if (batch.length >= 25) await flush();
    }
    await flush();

    if (changed) {
      this.revision++;
      for (const l of this.indexedListeners) l();
    }
  }

  /**
   * Graph projection. Drops `text` immediately so a large vault does not hold
   * a second copy of the corpus alive for the graph's sake.
   */
  async graphRows(): Promise<GraphIndexRow[]> {
    const rows = await this.db.noteIndex.toArray();
    return rows.map(toGraphRow);
  }

  /**
   * Same rows plus lowercased bodies. Only called when a live graph query has
   * bare terms, since those are the only thing that needs note content.
   */
  async graphRowsWithText(): Promise<{ rows: GraphIndexRow[]; corpus: Map<string, string> }> {
    const all = await this.db.noteIndex.toArray();
    const corpus = new Map<string, string>();
    for (const r of all) corpus.set(r.id, (r.text ?? "").toLowerCase());
    return { rows: all.map(toGraphRow), corpus };
  }

  /** Every tag in the workspace (item tags + content #hashtags) with counts. */
  async allTags(): Promise<Map<string, number>> {
    const counts = new Map<string, number>();
    const bump = (t: string) => counts.set(t, (counts.get(t) ?? 0) + 1);
    const rows = await this.db.noteIndex.toArray();
    for (const row of rows) {
      const seen = new Set([...(row.tags ?? []), ...(row.hashtags ?? [])].map((t) => t.toLowerCase()));
      for (const t of seen) bump(t);
    }
    return counts;
  }

  /** Note ids carrying a tag (item tag or content #hashtag), prefix match. */
  async notesWithTag(tagPrefix: string): Promise<string[]> {
    const q = tagPrefix.toLowerCase();
    const rows = await this.db.noteIndex.toArray();
    return rows
      .filter((r) =>
        [...(r.tags ?? []), ...(r.hashtags ?? [])].some((t) => t.toLowerCase().includes(q)),
      )
      .map((r) => r.id);
  }

  /** Note ids whose body wikilinks to the given title. */
  async backlinksTo(title: string): Promise<string[]> {
    const t = title.trim().toLowerCase();
    if (!t) return [];
    const rows = await this.db.noteIndex.where("links").equals(t).toArray();
    return rows.map((r) => r.id);
  }

  search(query: string, limit = 50): Promise<SearchHit[]> {
    if (!this.worker || !query.trim()) return Promise.resolve([]);
    const requestId = this.nextRequestId++;
    return new Promise((resolve) => {
      this.pending.set(requestId, resolve);
      this.post({ type: "search", requestId, query, limit });
    });
  }
}

export const searchService = new SearchService();
export type { SearchHit };
